/**
 * Pure model for the mobile appointment detail sheet + reschedule section.
 * No React. Event Dates carry salon wall-clock as browser-local time, so
 * days and minutes come from native local getters (same as grid/geometry).
 * API datetimes (suggested slots) go through parseApiDateTime.
 */
import { CalendarEventType } from "../../../../../types/calendar";
import { CalendarEvent } from "../../../../../types/transactions";
import { parseApiDateTime } from "../../../../../utils/timeUtils";
import type { SuggestedSlot } from "../../booking/useSuggestedTimes";
import {
  blockTimeLabel,
  DaySpan,
  dayKeyOf,
  daySpans,
  fmtMin,
  formatDate,
  longDayLabel,
  MobileAppt,
  processingBand,
} from "../grid/geometry";

/** Statuses that never occupy a provider (design `busy()`). */
const FREE_STATUSES = new Set(["Cancelled", "Declined"]);

const minutesOf = (d: Date): number => d.getHours() * 60 + d.getMinutes();

const lengthMin = (start: Date, end: Date): number =>
  Math.round((end.getTime() - start.getTime()) / 60000);

/** "Hydrafacial · 60 min". */
export const serviceRowText = (a: MobileAppt): string =>
  `${a.service} · ${a.endMin - a.startMin} min`;

/** "Monday, August 24 · 10:30 AM – 12:00 PM". */
export const whenRowText = (a: MobileAppt, start: Date): string =>
  `${longDayLabel(start)} · ${fmtMin(a.startMin)} – ${fmtMin(a.endMin)}`;

/** "11:00 AM – 11:30 AM is processing time · can be booked over", or null. */
export const processingRowText = (a: MobileAppt): string | null => {
  const band = processingBand(a);
  if (!band) return null;
  const from = a.startMin + band.offsetMin;
  return `${fmtMin(from)} – ${fmtMin(from + band.minutes)} is processing time · can be booked over`;
};

export interface StaffInterval {
  staffId: string;
  /** Salon-local day "YYYY-MM-DD". */
  day: string;
  /** Minutes from midnight. */
  startMin: number;
  endMin: number;
  /** Events of this transaction move with it: never a conflict. */
  excludeTransactionId?: string;
}

/**
 * Where an event occupies its provider on `day`, or undefined. A time block
 * covers every day it spans (`daySpans`, as the grids draw it), so a
 * Fri → Mon vacation is busy on Sat, Sun and Mon; an appointment only its
 * start day.
 */
function busySpanOn(e: CalendarEventType, day: string): DaySpan | undefined {
  const start = new Date(e.start);
  const end = new Date(e.end);
  if (e.isTimeBlock) return daySpans(start, end).find((s) => s.dayKey === day);
  if (dayKeyOf(start) !== day) return undefined;
  const startMin = minutesOf(start);
  return { dayKey: day, startMin, endMin: startMin + lengthMin(start, end) };
}

/**
 * The first calendar event (appointment or time block) that occupies
 * `staffId` during the interval, or null. Cancelled / Declined appointments
 * never occupy anyone.
 */
export function findStaffConflict(
  events: CalendarEventType[],
  q: StaffInterval,
): CalendarEventType | null {
  return (
    events.find((e) => {
      if (e.resourceId !== q.staffId) return false;
      if (!e.isTimeBlock) {
        const appt = e as CalendarEvent;
        if (FREE_STATUSES.has(appt.status)) return false;
        if (
          q.excludeTransactionId &&
          appt.transactionId === q.excludeTransactionId
        ) {
          return false;
        }
      }
      const span = busySpanOn(e, q.day);
      return !!span && q.startMin < span.endMin && span.startMin < q.endMin;
    }) || null
  );
}

/** "Fri, Oct 23, 9:00 AM". */
const dayTimeLabel = (d: Date): string =>
  `${formatDate(d, { weekday: "short", month: "short", day: "numeric" })}, ${fmtMin(minutesOf(d))}`;

/**
 * A conflicting event's time for overlap copy: "10:30 AM – 12:00 PM" within
 * one day. An event over several days (a vacation time block) names both
 * ends, "Fri, Oct 23, 9:00 AM – Mon, Oct 26, 5:00 PM": a one-day range
 * would show only its start day and hide that it covers the booking day.
 */
export function eventRangeText(e: CalendarEventType): string {
  const start = new Date(e.start);
  const end = new Date(e.end);
  if (daySpans(start, end).length > 1) {
    return `${dayTimeLabel(start)} – ${dayTimeLabel(end)}`;
  }
  const startMin = minutesOf(start);
  return blockTimeLabel({ startMin, endMin: startMin + lengthMin(start, end) });
}

/** Per-object conflict note (design copy, naming the provider). */
export const conflictNoteText = (providerName: string): string =>
  `${providerName} is busy then. Pick another time or provider.`;

/** "HH:mm" → minutes from midnight. */
export const toMinutes = (hhmm: string): number => {
  const [h, m] = (hhmm || "0:0").split(":").map(Number);
  return (h || 0) * 60 + (m || 0);
};

/** A suggested slot's salon-local day + time (UTC API datetime). */
export const slotParts = (
  slot: SuggestedSlot,
  timezone: string,
): { day: string; time: string } => {
  const m = parseApiDateTime(slot.start_dt, timezone);
  return { day: m.format("YYYY-MM-DD"), time: m.format("HH:mm") };
};

/** Suggested-time chip label: "Tue, Aug 25 · 10:00 AM". */
export const slotLabel = (slot: SuggestedSlot, timezone: string): string =>
  parseApiDateTime(slot.start_dt, timezone).format("ddd, MMM D · h:mm A");

/** Suggested times shown at once (design cap). */
export const MAX_SUGGESTIONS = 8;
